'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    addClientNote,
    deleteClientNote,
    getClient,
    Note,
} from '@/lib/api/clients/client-api'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import { MdDelete, MdNote, MdPerson } from 'react-icons/md'
import { toast } from 'react-toastify'
import { useProntuarioData } from './prontuario-context'

/**
 * Anotações livres sobre o paciente — as mesmas do cadastro (`Client.notes`), não
 * uma segunda lista.
 *
 * Estavam só na aba "Anotações" do FORMULÁRIO do cliente: para ler o que a recepção
 * anotou, o profissional tinha de abrir a tela de edição do cadastro, no meio dos
 * campos. Aqui elas ficam onde são lidas — junto do resto do prontuário, e por
 * tabela dentro do modal aberto durante o atendimento.
 */
export default function AnotacoesPage() {
    const { client, clientId } = useProntuarioData()
    const { user } = useAuth()

    const [notes, setNotes] = useState<Note[]>([])
    const [loading, setLoading] = useState(true)
    const [noteText, setNoteText] = useState('')
    const [saving, setSaving] = useState(false)
    const [indexToRemove, setIndexToRemove] = useState<number | null>(null)
    const [removing, setRemoving] = useState(false)

    useEffect(() => {
        // O paciente do contexto já traz as anotações — a tela abre preenchida. A
        // releitura logo em seguida cobre o caso de alguém ter escrito depois que o
        // prontuário foi carregado (e o que esta mesma tela gravou antes de trocar
        // de aba, já que o contexto não é recarregado a cada anotação).
        setNotes((client?.notes as Note[]) || [])
        setLoading(false)
        reloadNotes()
    }, [client])

    const reloadNotes = async () => {
        if (!clientId) return
        const response = await getClient(parseInt(clientId, 10))
        if (response.success && response.result) {
            setNotes((response.result.notes as Note[]) || [])
        }
    }

    const handleAdd = async () => {
        if (!clientId) return
        if (!noteText.trim()) {
            toast.error('Digite uma anotação')
            return
        }
        if (!user?.name) {
            toast.error('Não foi possível identificar o usuário')
            return
        }

        setSaving(true)
        try {
            const response = await addClientNote(
                parseInt(clientId, 10),
                noteText.trim(),
                user.name
            )
            if (response.success) {
                setNotes(response.result?.notes || [])
                setNoteText('')
                toast.success('Anotação adicionada')
            } else {
                toast.error(response.error || 'Erro ao adicionar anotação')
            }
        } catch (error) {
            toast.error('Erro ao adicionar anotação')
        } finally {
            setSaving(false)
        }
    }

    const confirmRemove = async () => {
        if (!clientId || indexToRemove === null) return

        setRemoving(true)
        try {
            const response = await deleteClientNote(
                parseInt(clientId, 10),
                indexToRemove
            )
            if (response.success) {
                toast.success('Anotação removida')
                await reloadNotes()
            } else {
                toast.error(response.error || 'Erro ao remover anotação')
            }
        } catch (error) {
            toast.error('Erro ao remover anotação')
        } finally {
            setRemoving(false)
            setIndexToRemove(null)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando anotações...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-base-content">
                        Anotações
                    </h2>
                    <p className="text-sm text-base-content/60">
                        Observações livres sobre o paciente
                    </p>
                </div>
                <span className="badge badge-lg badge-ghost">
                    {notes.length}{' '}
                    {notes.length === 1 ? 'anotação' : 'anotações'}
                </span>
            </div>

            {/* Nova anotação */}
            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-5">
                <label className="label pt-0">
                    <span className="label-text font-medium">
                        Nova anotação
                    </span>
                </label>
                <textarea
                    className="textarea textarea-bordered w-full focus:textarea-primary"
                    placeholder="Digite sua anotação aqui..."
                    value={noteText}
                    onChange={event => setNoteText(event.target.value)}
                    rows={4}
                />
                <div className="flex justify-end mt-4">
                    <button
                        type="button"
                        onClick={handleAdd}
                        className="btn btn-secondary btn-sm gap-2"
                        disabled={saving || !noteText.trim()}
                    >
                        {saving ? (
                            <>
                                <span className="loading loading-spinner loading-xs"></span>
                                Adicionando...
                            </>
                        ) : (
                            <>
                                <MdNote className="text-base" />
                                Adicionar anotação
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Lista */}
            {notes.length === 0 ? (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-8 text-center">
                    <MdNote className="text-4xl mx-auto mb-3 text-base-content/20" />
                    <p className="text-base-content/60">
                        Nenhuma anotação registrada
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {notes.map((note, index) => (
                        <div
                            key={index}
                            className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-sm p-5"
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="p-2 bg-secondary/10 rounded-lg">
                                            <MdNote className="text-lg text-secondary" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-semibold text-base-content truncate flex items-center gap-1">
                                                <MdPerson className="text-sm text-base-content/50" />
                                                {note.userName}
                                            </p>
                                            {note.createdAt && (
                                                <p className="text-xs text-base-content/50">
                                                    {new Date(
                                                        note.createdAt
                                                    ).toLocaleString('pt-BR')}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <p className="text-sm text-base-content/80 whitespace-pre-wrap">
                                        {note.note}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setIndexToRemove(index)}
                                    className="btn btn-ghost btn-sm btn-square text-error"
                                    title="Remover anotação"
                                >
                                    <MdDelete className="text-lg" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ConfirmModal
                isOpen={indexToRemove !== null}
                onClose={() => setIndexToRemove(null)}
                onConfirm={confirmRemove}
                loading={removing}
                title="Remover anotação"
                message="Tem certeza de que deseja remover esta anotação? Esta ação não pode ser desfeita."
                confirmButtonText="Remover"
            />
        </div>
    )
}
